import { useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Connection, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { getSolanaEndpoint } from '@/lib/solana/config';
import { WebSocketService } from '@/services/ws/WebSocketService';

// Debounce delay for WebSocket account change callbacks
const WS_DEBOUNCE_MS = 1000;

// Shared connection instance to avoid creating new ones on every fetch
let sharedConnection: Connection | null = null;
function getSharedConnection(): Connection {
  if (!sharedConnection) {
    sharedConnection = new Connection(getSolanaEndpoint(), 'confirmed');
  }
  return sharedConnection;
}

// Query key factory
const walletBalanceKeys = {
  balance: (walletAddress: string | null) => ['walletBalance', walletAddress] as const,
};

// Fetch function - used by React Query
async function fetchWalletBalance(publicKey: PublicKey): Promise<{ balance: number; balanceLamports: bigint }> {
  const connection = getSharedConnection();
  const balanceLamports = await connection.getBalance(publicKey);
  const balance = balanceLamports / LAMPORTS_PER_SOL;
  return { balance, balanceLamports: BigInt(balanceLamports) };
}

export const useWalletBalance = () => {
  const { publicKey, connected, walletAddress, onWalletChange } = useTurnkeyWallet();
  const queryClient = useQueryClient();

  // Use React Query for balance fetching with automatic deduplication
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: walletBalanceKeys.balance(walletAddress),
    queryFn: () => {
      if (!publicKey) throw new Error('No public key');
      return fetchWalletBalance(publicKey);
    },
    enabled: connected && !!publicKey && !!walletAddress,
    staleTime: 0, // Tier 1: real-time
    refetchInterval: 30_000, // Poll every 30 seconds (reduced from 15s)
    refetchIntervalInBackground: false, // Don't poll when tab is hidden
    retry: 2,
  });

  // Subscribe to wallet change events for immediate refetch
  useEffect(() => {
    if (!onWalletChange) return;

    const unsubscribe = onWalletChange(() => {
      // Invalidate cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: walletBalanceKeys.balance(walletAddress) });
    });

    return unsubscribe;
  }, [onWalletChange, walletAddress, queryClient]);

  // WebSocket accountSubscribe for instant balance detection (~500ms vs 30s polling)
  const wsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!walletAddress || !connected) return;

    const service = WebSocketService.getInstance();
    // Only subscribe if the WS is connected (or will resubscribe on reconnect)
    const unsub = service.subscribeAccount(walletAddress, () => {
      // Debounce to avoid rapid-fire refetches on busy wallets
      if (wsDebounceRef.current) clearTimeout(wsDebounceRef.current);
      wsDebounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: walletBalanceKeys.balance(walletAddress) });
      }, WS_DEBOUNCE_MS);
    });

    return () => {
      unsub();
      if (wsDebounceRef.current) {
        clearTimeout(wsDebounceRef.current);
        wsDebounceRef.current = null;
      }
    };
  }, [walletAddress, connected, queryClient]);

  // Memoize return value to prevent unnecessary re-renders
  return useMemo(() => ({
    balance: data?.balance ?? null,
    balanceLamports: data?.balanceLamports ?? null,
    isLoading: isLoading && !data, // Only show loading on initial fetch
    isInitialLoad: isLoading && !data,
    isFetching, // True during any fetch (initial or refetch)
    error: error instanceof Error ? error.message : null,
    refetch,
    formattedBalance: data?.balance !== undefined ? data.balance.toFixed(4) : '—',
  }), [data, isLoading, isFetching, error, refetch]);
};
